/**
 * DKPLAT-618: leaderboards only show the Cash / Crowns columns when the
 * pick'em's payout template actually uses that currency. The backend returns a
 * `visibleCurrencies` flag; here we map it to the (static, Tailwind-scannable)
 * grid-template classes for each leaderboard layout.
 *
 * The class strings MUST be written out in full — Tailwind's JIT only generates
 * arbitrary `grid-cols-[…]` values it can see literally in the source, so these
 * cannot be built by interpolation.
 */

export interface VisibleCurrencies {
  cash: boolean;
  crowns: boolean;
}

/** Fail-open default: show both, matching pre-DKPLAT-618 behaviour. */
export const DEFAULT_VISIBLE_CURRENCIES: VisibleCurrencies = { cash: true, crowns: true };

const key = (v: VisibleCurrencies) => `${v.cash}-${v.crowns}`;

/**
 * Per-pick'em / per-week breakdown lists and their header
 * (UserPickemsList, WeeklyBreakdownList, LeaderboardSection breakdown header).
 * Columns: label(1fr) · rank · [cash] · [crowns, sm+] · other(sm+) · points.
 * Cash is the only currency shown on mobile; crowns/other are `sm:` only.
 */
const BREAKDOWN_GRID: Record<string, string> = {
  'true-true': 'grid-cols-[1fr_40px_55px_40px] sm:grid-cols-[1fr_50px_70px_70px_120px_50px]',
  'true-false': 'grid-cols-[1fr_40px_55px_40px] sm:grid-cols-[1fr_50px_70px_120px_50px]',
  'false-true': 'grid-cols-[1fr_40px_40px] sm:grid-cols-[1fr_50px_70px_120px_50px]',
  'false-false': 'grid-cols-[1fr_40px_40px] sm:grid-cols-[1fr_50px_120px_50px]',
};

export const breakdownGridClass = (v: VisibleCurrencies): string => BREAKDOWN_GRID[key(v)];

/**
 * Pick'em leaderboard table (PickemLeaderboard header + rows).
 * Columns: rank(40px) · user(1fr) · [cash] · [crowns, sm+] · other(sm+) · pts · expand(24px).
 */
const PICKEM_LEADERBOARD_GRID: Record<string, string> = {
  'true-true':
    'grid-cols-[40px_1fr_60px_50px_24px] sm:grid-cols-[40px_1fr_70px_70px_140px_50px_24px]',
  'true-false': 'grid-cols-[40px_1fr_60px_50px_24px] sm:grid-cols-[40px_1fr_70px_140px_50px_24px]',
  'false-true': 'grid-cols-[40px_1fr_50px_24px] sm:grid-cols-[40px_1fr_70px_140px_50px_24px]',
  'false-false': 'grid-cols-[40px_1fr_50px_24px] sm:grid-cols-[40px_1fr_140px_50px_24px]',
};

export const pickemLeaderboardGridClass = (v: VisibleCurrencies): string =>
  PICKEM_LEADERBOARD_GRID[key(v)];
